import React from "react";
import "./HTML_MAIN_CSS.css";

const HTMLJavascript = () => {
  return (
    <div className="html-container">
      <h1>HTML – JavaScript</h1>

      {/* 1. Introduction */}
      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          JavaScript is a scripting language used to create dynamic and interactive content in web pages.
          It allows you to manipulate HTML elements, respond to events, validate forms, and more.
        </p>
      </section>

      {/* 2. Adding JavaScript */}
      <section id="adding-js">
        <h2>2. Adding JavaScript to HTML</h2>
        <p>There are three main ways to add JavaScript:</p>
        <ul>
          <li><strong>Inline:</strong> Directly in the HTML element using <code>onclick</code> or other event attributes.</li>
          <li><strong>Internal:</strong> Using a <code>&lt;script&gt;</code> tag inside the HTML file.</li>
          <li><strong>External:</strong> Linking a separate <code>.js</code> file using <code>&lt;script src="file.js"&gt;</code>.</li>
        </ul>
      </section>

      {/* 3. Inline JavaScript */}
      <section id="inline-js">
        <h2>3. Inline JavaScript</h2>
        <p>Example using the <code>onclick</code> event:</p>
        <pre>
          <code>{`<button onclick="alert('Hello World!')">Click Me</button>`}</code>
        </pre>
      </section>

      {/* 4. Internal JavaScript */}
      <section id="internal-js">
        <h2>4. Internal JavaScript</h2>
        <p>Example using a <code>&lt;script&gt;</code> tag in the HTML:</p>
        <pre>
          <code>{`<script>
function greet() {
  alert('Hello from internal JavaScript!');
}
</script>

<button onclick="greet()">Click Me</button>`}</code>
        </pre>
      </section>

      {/* 5. External JavaScript */}
      <section id="external-js">
        <h2>5. External JavaScript</h2>
        <p>Example linking an external JavaScript file:</p>
        <pre>
          <code>{`<!-- index.html -->
<script src="script.js"></script>

<button onclick="greet()">Click Me</button>

<!-- script.js -->
function greet() {
  alert('Hello from external JavaScript!');
}`}</code>
        </pre>
      </section>

      {/* 6. Common JavaScript Features */}
      <section id="features">
        <h2>6. Common JavaScript Features</h2>
        <ul>
          <li>Manipulating HTML elements with <code>document.getElementById</code> or <code>querySelector</code>.</li>
          <li>Responding to events like <code>click</code>, <code>mouseover</code>, <code>keydown</code>, etc.</li>
          <li>Form validation and input handling.</li>
          <li>Using variables, functions, loops, and conditional statements.</li>
          <li>Interacting with APIs using <code>fetch</code> or <code>XMLHttpRequest</code>.</li>
        </ul>
      </section>

      {/* 7. Example: Changing Content */}
      <section id="example-content">
        <h2>7. Example: Changing HTML Content</h2>
        <p>Click the button to change the text:</p>
        <p id="demo-text">Original Text</p>
        <button
          onClick={() => {
            const text = document.getElementById("demo-text");
            text.innerHTML = "Text changed by JavaScript!";
          }}
        >
          Change Text
        </button>
      </section>

      {/* 8. Best Practices */}
      <section id="best-practices">
        <h2>8. Best Practices</h2>
        <ul>
          <li>Keep JavaScript separate in external files for maintainability.</li>
          <li>Use descriptive function and variable names.</li>
          <li>Minimize inline JavaScript for cleaner HTML.</li>
          <li>Use event listeners instead of inline <code>onclick</code> attributes:
            <pre>
              <code>{`document.getElementById('myButton').addEventListener('click', greet);`}</code>
            </pre>
          </li>
          <li>Ensure accessibility and browser compatibility.</li>
        </ul>
      </section>

      {/* 9. Summary */}
      <section id="summary">
        <h2>9. Summary</h2>
        <ul>
          <li>JavaScript enables dynamic, interactive behavior on web pages.</li>
          <li>It can be added inline, internally, or externally.</li>
          <li>Best practices include separating code, using event listeners, and writing maintainable scripts.</li>
        </ul>
      </section>
    </div>
  );
};

export default HTMLJavascript;
